import React from 'react';
import styled from 'styled-components';

import {
  Colors,
  DataStore,
  FontSizes,
  FontFamilies,
  Theme,
  isValidEmail,
  Image,
} from 'common';

import {
  Button,
  Checkbox,
  DateSelect,
  Stepper,
  Text,
  TextInput,
  Select,
} from 'common/components';

import { withCommon } from 'common/hocs';

import {
  EventName,
  MaxWidthContainer,
  StepperContainer,
} from 'ui/event/styled';

import { HtmlModal } from 'ui/components';

import { getCheckoutSteps, needsCustomFieldsStep } from 'ui/event/eventUtils';

/** Build per-priceId -> instance index -> field id -> field with value */
const buildInitialPerTicketFieldState = (selectedTickets, authenticatedContact) => {
  const result = {};
  const lines = selectedTickets?.selectedTickets;
  if (!lines || typeof lines !== 'object') {
    return result;
  }

  for (const line of Object.values(lines)) {
    if (!line?.id || line.count <= 0 || !line.custom_fields?.length) {
      continue;
    }

    const priceId = line.id;
    result[priceId] = {};
    const existingSlots = Array.isArray(line.per_ticket_custom_fields)
      ? line.per_ticket_custom_fields
      : [];

    for (let i = 0; i < line.count; i += 1) {
      result[priceId][i] = {};
      const existingRow = Array.isArray(existingSlots[i]) ? existingSlots[i] : [];

      line.custom_fields.forEach((field) => {
        const fromExisting = existingRow.find((f) => f.id === field.id);
        let value = fromExisting?.value;

        if (
          (value === undefined || value === null || value === '') &&
          authenticatedContact
        ) {
          if (field.type === 'email' && isValidEmail(authenticatedContact)) {
            value = authenticatedContact;
          } else if (field.type === 'tel' && !isValidEmail(authenticatedContact)) {
            value = authenticatedContact;
          }
        }

        if (value === undefined || value === null) {
          value = field.type === 'checkbox' ? false : '';
        }

        result[priceId][i][field.id] = {
          ...field,
          value,
        };
      });
    }
  }

  return result;
};

const Divide = styled.div`
  background-color: ${Theme.EventIdentity.DividerColor};
  height: 1px;
  width: 100%;
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 0 0 0;
`;

const ButtonSpace = styled.div`
  width: 20px;
`;

const FullWidthContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const CustomFieldContainer = styled.div`
  margin: 15px 0;
  width: 100%;
`;

const CustomFieldLabel = styled(Text)`
  display: block;
  padding: 0 0 5px 0;
  
  ${props => props.required && `
    &:after {
      content: ' *';
      color: ${Colors.Error};
    }
  `}
`;

const CustomFieldImageContainer = styled.div`
  margin: 20px 0 10px 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CustomFieldImage = styled(Image)`
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 5px;
`;

const TermsLink = styled(Text)`
  color: ${Colors.PrimaryOne || '#0066cc'};
  font-family: ${FontFamilies.Bold};
  text-decoration: underline;
  cursor: pointer;
  display: inline;
  margin-left: 5px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const PerTicketSlotHeading = styled(Text)`
  display: block;
  font-family: ${FontFamilies.Bold};
  font-size: ${FontSizes.Small};
  margin: ${props => (props.$isFirst ? '20px' : '14px')} 0 8px 0;
`;

const EventCustomFieldsUI = props => {

  const [state, setState] = React.useState(() => {
    const event = DataStore.get('selectedEvent') || {};
    const authenticatedContact = DataStore.get('LOGGED_IN_EMAIL');
    const selectedTickets = DataStore.get('selectedTickets');
    const customFields = {};

    // Prepopulate custom fields with existing values or authenticated contact
    if (event.custom_fields) {
      event.custom_fields.forEach(field => {
        // First check if field already has a value
        if (field.value !== undefined && field.value !== null && field.value !== '') {
          customFields[field.id] = {
            ...field,
            value: field.value
          };
        } 
        // If no existing value, try to prepopulate email/phone fields with authenticated contact
        else if (authenticatedContact) {
          if (field.type === 'email' && isValidEmail(authenticatedContact)) {
            customFields[field.id] = {
              ...field,
              value: authenticatedContact
            };
          } else if (field.type === 'tel' && !isValidEmail(authenticatedContact)) {
            // If not an email, assume it's a phone number
            customFields[field.id] = {
              ...field,
              value: authenticatedContact
            };
          }
        }
      });
    }

    return {
      event,
      customFields,
      perTicketCustomFields: buildInitialPerTicketFieldState(
        selectedTickets,
        authenticatedContact
      ),
    };
  });

  const [showTermsModal, setShowTermsModal] = React.useState(false);

  const validateCustomField = (field, value) => {
    if (field.required && (!value || (Array.isArray(value) && !value.length) || (typeof value === 'string' && !value.trim()))) {
      return `${field.label} is required`;
    }

    if (!value || (typeof value === 'string' && !value.trim())) return null; // Don't validate empty optional fields

    // temp fix for boland
    if (field.label?.startsWith('Smart Shopper number')) {

      let ssValue = typeof value === 'string' ? value.trim() : '';
      
      if (ssValue.length !== 16) {
        return 'Smart Shopper number must be 16 digits';
      }

      if ("7353" !== ssValue.substring(0, 4)) {
        return 'Smart Shopper number is invalid, please confirm the number entered is correct';
      }
    }

    switch (field.type) {
      case 'email':
        return isValidEmail(value) ? null : `${field.label} must be a valid email`;
      
      case 'tel':
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        return phoneRegex.test(value) ? null : `${field.label} must be a valid phone number`;
      
      case 'number':
        return !isNaN(value) ? null : `${field.label} must be a valid number`;

      case 'date': {
        const str = typeof value === 'string' ? value.trim() : '';
        if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) {
          return `${field.label} must be a valid date`;
        }
        const t = new Date(`${str}T12:00:00`).getTime();
        return !Number.isNaN(t) ? null : `${field.label} must be a valid date`;
      }
      
      default:
        return null;
    }
  };

  const validateCustomFields = () => {
    const errors = [];

    if (state.event?.custom_fields?.length) {
      state.event.custom_fields.forEach((field) => {
        const error = validateCustomField(
          field,
          state.customFields?.[field.id]?.value
        );
        if (error) errors.push(error);
      });
    }

    const basket = DataStore.get('selectedTickets');
    const lines = basket?.selectedTickets;
    if (lines && typeof lines === 'object') {
      for (const line of Object.values(lines)) {
        if (
          !line?.id ||
          line.count <= 0 ||
          !line.custom_fields?.length
        ) {
          continue;
        }

        const priceId = line.id;
        for (let i = 0; i < line.count; i += 1) {
          for (const field of line.custom_fields) {
            const stored =
              state.perTicketCustomFields[priceId]?.[i]?.[field.id];
            const error = validateCustomField(field, stored?.value);
            if (error) errors.push(error);
          }
        }
      }
    }

    if (errors.length > 0) {
      props.common.showToast(errors.join('. '));
      return false;
    }

    return true;
  };

  const onCustomFieldChange = (field, value) => {
    if (!field) return;
    if (!field.id) field.id = new Date().getTime();

    setState(prevState => ({
      ...prevState,
      customFields: {
        ...prevState.customFields,
        [field.id]: {
          ...field,
          value: value
        }
      }
    }));
  };

  const onPerTicketFieldChange = (priceId, instanceIndex, field, value) => {
    if (!field) return;
    if (!field.id) field.id = new Date().getTime();

    setState((prevState) => ({
      ...prevState,
      perTicketCustomFields: {
        ...prevState.perTicketCustomFields,
        [priceId]: {
          ...prevState.perTicketCustomFields[priceId],
          [instanceIndex]: {
            ...prevState.perTicketCustomFields[priceId]?.[instanceIndex],
            [field.id]: {
              ...field,
              value,
            },
          },
        },
      },
    }));
  };

  const getFieldTypePlaceholder = (type) => {
    const typeLabel = {
      tel: 'phone',
      textarea: 'text',
    }[type] || type || 'text';

    return `Enter ${typeLabel}...`;
  };

  const renderCustomFieldControl = (field, value, onValueChange) => {
    const commonProps = {
      width: '100%',
      value: value === undefined || value === null ? '' : value,
      onChange: (v) => onValueChange(v),
      placeholder: field.placeholder || getFieldTypePlaceholder(field.type),
    };

    switch (field.type) {
      case 'textarea':
        return (
          <TextInput
            {...commonProps}
            multiline={true}
          />
        );

      case 'number':
        return (
          <TextInput
            {...commonProps}
            type="number"
          />
        );

      case 'email':
        return (
          <TextInput
            {...commonProps}
            type="email"
          />
        );

      case 'tel':
        return (
          <TextInput
            {...commonProps}
            type="tel"
          />
        );

      case 'date':
        return (
          <DateSelect
            width={commonProps.width}
            value={commonProps.value}
            onChange={commonProps.onChange}
          />
        );

      case 'checkbox':
        return (
          <Checkbox
            checked={!!value}
            label={field.placeholder || ''}
            onClick={(checked) => onValueChange(checked)}
          />
        );

      case 'select':
        return (
          <Select
            {...commonProps}
            options={field.options.map(opt => ({
              value: opt,
              label: opt
            }))}
            placeholder={`Select ${field.label.toLowerCase()}...`}
          />
        );

      default:
        return (
          <TextInput
            {...commonProps}
            type="text"
          />
        );
    }
  };

  const renderEventFieldBlock = (field, index) => {
    const imageUrl = field.image || field.image_url || field.imageUrl;
    const value = state.customFields?.[field.id]?.value || '';

    return (
      <CustomFieldContainer key={`custom-field-${index}`}>
        {field.label?.startsWith('Smart Shopper number') && (
          <CustomFieldImageContainer>
            <CustomFieldImage
              src={imageUrl || 'https://storage.googleapis.com/ticketing-files/events-products/pnp_smart.png'}
              alt={field.label || 'Custom field image'}
            />
          </CustomFieldImageContainer>
        )}
        <CustomFieldLabel
          fontSize={FontSizes.Smaller}
          required={field.required}>
          {field.label}
        </CustomFieldLabel>
        {field.description && (
          <Text
            fontSize={FontSizes.Smallest}
            color={Colors.Grey}
            display={'block'}
            padding={'0 0 5px 0'}>
            {field.description}
            {field.label?.startsWith('Smart Shopper number') && (
              <TermsLink
                fontSize={FontSizes.Smaller}
                onClick={() => setShowTermsModal(true)}>
                Terms and Conditions
              </TermsLink>
            )}
          </Text>
        )}
        {renderCustomFieldControl(field, value, (v) =>
          onCustomFieldChange(field, v)
        )}
      </CustomFieldContainer>
    );
  };

  const renderPerTicketFieldBlock = (field, index, priceId, instanceIndex) => {
    const imageUrl = field.image || field.image_url || field.imageUrl;
    const stored =
      state.perTicketCustomFields[priceId]?.[instanceIndex]?.[field.id];
    const value =
      stored?.value !== undefined && stored?.value !== null
        ? stored.value
        : field.type === 'checkbox'
          ? false
          : '';

    return (
      <CustomFieldContainer
        key={`pt-${priceId}-${instanceIndex}-${field.id}-${index}`}>
        {field.label?.startsWith('Smart Shopper number') && (
          <CustomFieldImageContainer>
            <CustomFieldImage
              src={imageUrl || 'https://storage.googleapis.com/ticketing-files/events-products/pnp_smart.png'}
              alt={field.label || 'Custom field image'}
            />
          </CustomFieldImageContainer>
        )}
        <CustomFieldLabel
          fontSize={FontSizes.Smaller}
          required={field.required}>
          {field.label}
        </CustomFieldLabel>
        {field.description && (
          <Text
            fontSize={FontSizes.Smallest}
            color={Colors.Grey}
            display={'block'}
            padding={'0 0 5px 0'}>
            {field.description}
            {field.label?.startsWith('Smart Shopper number') && (
              <TermsLink
                fontSize={FontSizes.Smaller}
                onClick={() => setShowTermsModal(true)}>
                Terms and Conditions
              </TermsLink>
            )}
          </Text>
        )}
        {renderCustomFieldControl(field, value, (v) =>
          onPerTicketFieldChange(priceId, instanceIndex, field, v)
        )}
      </CustomFieldContainer>
    );
  };

  const renderEventCustomFields = () => {
    if (!state.event?.custom_fields?.length) return null;

    return state.event.custom_fields.map((field, index) =>
      renderEventFieldBlock(field, index)
    );
  };

  const renderPerTicketTypeCustomFields = () => {
    const basket = DataStore.get('selectedTickets');
    const lines = basket?.selectedTickets;
    if (!lines || typeof lines !== 'object') {
      return null;
    }

    const blocks = [];

    for (const line of Object.values(lines)) {
      if (
        !line?.id ||
        line.count <= 0 ||
        !line.custom_fields?.length
      ) {
        continue;
      }

      const priceId = line.id;
      const typeTitle =
        line.title || line.name || line.label || 'Ticket';

      blocks.push(
        <React.Fragment key={`ticket-type-${priceId}`}>
          {Array.from({ length: line.count }, (_, instanceIndex) => (
            <React.Fragment key={`ticket-${priceId}-${instanceIndex}`}>
              <PerTicketSlotHeading $isFirst={instanceIndex === 0}>
                #{instanceIndex + 1} {typeTitle}
              </PerTicketSlotHeading>
              {line.custom_fields.map((field, fIndex) =>
                renderPerTicketFieldBlock(
                  field,
                  fIndex,
                  priceId,
                  instanceIndex
                )
              )}
            </React.Fragment>
          ))}
        </React.Fragment>
      );
    }

    return blocks.length ? blocks : null;
  };

  const onSubmitClick = () => {
    if (!validateCustomFields()) {
      return;
    }

    const nextEvent = { ...state.event };

    if (state.event.custom_fields?.length) {
      const updatedCustomFields = state.event.custom_fields.map((field) => {
        if (state.customFields[field.id]) {
          return state.customFields[field.id];
        }
        return field;
      });
      nextEvent.custom_fields = updatedCustomFields;
    }

    DataStore.set('selectedEvent', nextEvent);

    const basket = DataStore.get('selectedTickets') || {};
    const lines = basket.selectedTickets;
    if (lines && typeof lines === 'object') {
      const nextSelectedTickets = {
        ...basket,
        selectedTickets: { ...lines },
      };

      for (const line of Object.values(lines)) {
        if (
          !line?.id ||
          line.count <= 0 ||
          !line.custom_fields?.length
        ) {
          continue;
        }

        const priceId = line.id;
        const perTicket = [];
        for (let i = 0; i < line.count; i += 1) {
          const row = line.custom_fields.map((fieldDef) => {
            const stored =
              state.perTicketCustomFields[priceId]?.[i]?.[fieldDef.id];
            if (stored) {
              return stored;
            }
            return {
              ...fieldDef,
              value:
                fieldDef.type === 'checkbox' ? false : '',
            };
          });
          perTicket.push(row);
        }

        nextSelectedTickets.selectedTickets[priceId] = {
          ...line,
          per_ticket_custom_fields: perTicket,
        };
      }

      DataStore.set('selectedTickets', nextSelectedTickets);
    }

    if (nextEvent.has_seating) {
      const expiryTime = new Date(Date.now() + 10 * 60 * 1000).getTime();
      props.common.navigate(`/event/seats?expiry=${expiryTime}`);
      return;
    }

    props.common.navigate('/event/payment');
  };

  const onBackClick = () => {
    props.common.history.goBack();
  };

  React.useEffect(() => {
    window.pnpTermsTimestamp = new Date().getTime();
  }, []);

  React.useEffect(() => {
    const event = DataStore.get('selectedEvent') || {};
    const selectedTickets = DataStore.get('selectedTickets');
    if (event.has_seating) {
      return;
    }
    if (!needsCustomFieldsStep(event, selectedTickets)) {
      props.common.navigate('/event/payment', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time guard when landing on this route
  }, []);

  return (
    <>
      <Text
        maxWidth={'1100px'}
        margin={'0 auto'}
        width={'100%'}
        display={'block'} 
        padding={'20px 15px 0 15px'} 
        fontSize={FontSizes.Small}
        color={Theme.Text.Color}
      >
        <span style={{cursor: 'pointer'}} onClick={() => props.common.navigate(-1)}>← Back</span>
      </Text>

      <EventName>{state.event.name}</EventName>

      <Text display={'block'} padding={'0 15px 5px 15px'} textAlign={'center'} fontSize={FontSizes.Small}>{state.event.location_display}</Text>

      { !state.event.is_multi_event &&
        <Text display={'block'} padding={'0 0 20px 0'} textAlign={'center'} fontSize={FontSizes.Small}>{state.event.displayDate}</Text>
      }
      { state.event.is_multi_event &&
        <Text display={'block'} padding={'0 0 30px 0'} textAlign={'center'} fontSize={FontSizes.Small}></Text>
      }

      <FullWidthContainer>

        <StepperContainer>
          <Stepper
            items={getCheckoutSteps(state.event)}
            selectedIndex={1}
          />
        </StepperContainer>

        <MaxWidthContainer>

          <Text
            display={'block'}
            fontFamily={FontFamilies.Bold}
            fontSize={FontSizes.Big}>
              Enter Details
          </Text>

          <Text
            display={'block'}
            fontSize={FontSizes.Smaller}
            padding={'0 0 15px 0'}>
              Please provide the following information to complete your ticket purchase.
          </Text>

          <Divide/>

          {renderEventCustomFields()}
          {renderPerTicketTypeCustomFields()}

        </MaxWidthContainer>

        <div style={{ flex: 1, minHeight: props.common.isWidget ? '30px' : 'auto' }} />

        <MaxWidthContainer padding={'0'}>

          <ActionContainer>

            <MaxWidthContainer>

              <Divide/>

              <ActionButtonContainer>

                <Button onClick={onBackClick} backgroundColor={Theme.Button.Secondary.Background}>Back</Button>

                <ButtonSpace/>

                <Button onClick={onSubmitClick}>Continue</Button>

              </ActionButtonContainer>

            </MaxWidthContainer>

          </ActionContainer>

        </MaxWidthContainer>

      </FullWidthContainer>

      <HtmlModal 
        url={'https://storage.googleapis.com/website777/weTicket/boland_rugby/legal/termsc_pickpayV2.html?v=' + (window.pnpTermsTimestamp || '1')} 
        show={showTermsModal} 
        onClose={() => setShowTermsModal(false)}
      />

    </>
  );
};

export const EventCustomFields = withCommon(
  EventCustomFieldsUI, {
    showLoadingOnLoad: false
  }
); 